import React from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatInrWithoutDecimal } from "../../utils/formatWithoutDecimal";
import { useDashboardValuePrivacy } from "../../hooks/useDashboardValuePrivacy";
import PrivacyBlur from "../PrivacyBlur";

const MONTH_NAMES = {
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec",
};

const TotalSettlements = ({ className = "", salesData }) => {
  const { valuesHidden } = useDashboardValuePrivacy();
  const monthwise = Array.isArray(salesData?.monthwise_sales_current_financial_year)
    ? salesData.monthwise_sales_current_financial_year
    : [];
  const settlementSeries = monthwise.map((item) => ({
    name: MONTH_NAMES[item?.calendar_month] || String(item?.calendar_month || ""),
    value: Number(item?.total_sales ?? 0),
  }));

  const currentFYLabel = salesData?.financial_year_label;
  const yearlyRows = Array.isArray(salesData?.yearly_sales_financial_year_wise)
    ? salesData.yearly_sales_financial_year_wise
    : [];
  const yearlySalesTotal = Number(
    (yearlyRows.find((row) => row?.fiscal_year_label === currentFYLabel)?.total_sales ??
      yearlyRows[0]?.total_sales ??
      settlementSeries.reduce((sum, item) => sum + Number(item.value || 0), 0)),
  );
  const thisMonthValue = Number(salesData?.this_month_sales ?? 0);
  const lastMonthValue = Number(salesData?.last_month_sales ?? 0);
  const safeSeries =
    settlementSeries.length > 0 ? settlementSeries : [{ name: "Apr", value: 0 }];

  return (
    <div className={`col-lg-3 col-md-5 col-12 ${className}`.trim()}>
      <div
        className="card shadow-none border-0"
        style={{
          minHeight: "285px",
          height: "285px",
          overflow: "hidden",
          borderRadius: "18px",
          background: "#FFFFFF",
          border: "1px solid #E2E8F0",
        }}
      >
        <div className="card-body px-4 pt-4 pb-3">
          <div className="d-flex align-items-center gap-3 mb-3">
            <div>
              <p
                className="mb-1"
                style={{
                  fontSize: "0.85rem",
                  color: "#475569",
                  fontWeight: 500,
                }}
              >
                Yearly Sales
              </p>
              <h5
                className="mb-0"
                style={{
                  fontSize: "1.55rem",
                  fontWeight: 700,
                  color: "#0F172A",
                }}
              >
                <PrivacyBlur hidden={valuesHidden}>
                  {formatInrWithoutDecimal(yearlySalesTotal)}
                </PrivacyBlur>
              </h5>
            </div>
          </div>

          <div style={{ height: 110 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={safeSeries}
                margin={{ top: 4, right: 0, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient
                    id="settlementFill"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#2563EB" stopOpacity={0.28} />
                    <stop offset="95%" stopColor="#2563EB" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#D8E4FF"
                />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis hide domain={["dataMin - 6", "dataMax + 6"]} />
                <Tooltip
                  formatter={(val) => [
                    formatInrWithoutDecimal(val),
                    "Monthly Sales",
                  ]}
                  contentStyle={{
                    borderRadius: "10px",
                    border: "1px solid #DBEAFE",
                    boxShadow: "0 8px 20px rgba(37, 99, 235, 0.12)",
                    filter: valuesHidden ? "blur(8px)" : undefined,
                    userSelect: valuesHidden ? "none" : undefined,
                  }}
                  cursor={{ stroke: "#BFDBFE", strokeWidth: 1 }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#2563EB"
                  strokeWidth={2.2}
                  fill="url(#settlementFill)"
                  dot={false}
                  isAnimationActive
                  animationDuration={850}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="row mt-2 g-0">
            <div className="col-6 text-center">
              <p
                className="mb-1"
                style={{ color: "#64748B", fontSize: "0.78rem" }}
              >
                This month
              </p>
              <h6
                className="mb-0"
                style={{ color: "#0F172A", fontWeight: 700 }}
              >
                <PrivacyBlur hidden={valuesHidden}>
                  {formatInrWithoutDecimal(thisMonthValue)}
                </PrivacyBlur>
              </h6>
            </div>
            <div className="col-6 text-center">
              <p
                className="mb-1"
                style={{ color: "#64748B", fontSize: "0.78rem" }}
              >
                Last Month
              </p>
              <h6
                className="mb-0"
                style={{ color: "#0F172A", fontWeight: 700 }}
              >
                <PrivacyBlur hidden={valuesHidden}>
                  {formatInrWithoutDecimal(lastMonthValue)}
                </PrivacyBlur>
              </h6>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TotalSettlements;
